import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  fetchTaskThread,
  gmailThreadUrl,
  retryTaskAcquisition,
  type TaskThread,
  type ThreadMessage,
} from '../api/tasks'
import { useTasks, dueDateInputValue, extraInstructions } from '../hooks/useTasks'
import { formatWhen, relativeTime } from '../lib/time'
import type { EmailTask, TaskPriority, TaskStatus } from '../types'
import { EllisExecuteModal } from './EllisExecuteModal'
import { SubtasksSection } from './SubtasksSection'
import { TaskShares } from './TaskShares'

function statusLabel(status: TaskStatus): string {
  if (status === 'wrong_redo') return 'Wrong / Redo'
  return status
}

function ThreadMessageBox({
  message,
  isSource,
}: {
  message: ThreadMessage
  isSource: boolean
}) {
  const who =
    message.from_name && message.from_name.trim()
      ? `${message.from_name} <${message.from_address}>`
      : message.from_address
  return (
    <article
      className={`thread-msg dir-${message.direction}${isSource ? ' is-source' : ''}`}
    >
      <header>
        <span className="dir">{message.direction}</span>
        {isSource ? <span className="source-tag">todo source</span> : null}
        <span className="who" title={who}>
          {who}
        </span>
        <time dateTime={message.sent_at ?? undefined}>{formatWhen(message.sent_at)}</time>
      </header>
      {message.subject ? <p className="subj">{message.subject}</p> : null}
      <pre className="thread-body" tabIndex={0}>
        {message.redacted
          ? '(message body hidden — monitor access)'
          : message.body_text || '(no body text stored)'}
      </pre>
    </article>
  )
}

/** Best-effort: which stored message likely originated this todo's quote. */
function findSourceMessageId(
  quote: string,
  messages: ThreadMessage[],
): string | null {
  const q = quote.trim().toLowerCase()
  if (!q || !messages.length) return null
  let bestId: string | null = null
  let best = 0
  for (const m of messages) {
    const body = (m.body_text || '').toLowerCase()
    if (!body) continue
    let score = 0
    if (body.includes(q)) {
      score = 1000 + q.length
    } else {
      const words = q.split(/\s+/).filter((w) => w.length > 3)
      if (!words.length) continue
      let hits = 0
      for (const w of words) if (body.includes(w)) hits += 1
      score = hits
    }
    if (score > best) {
      best = score
      bestId = m.id
    }
  }
  return best > 0 ? bestId : null
}

/**
 * Up to `windowSize` messages ending at the todo source (chronological),
 * returned newest-first for display. Falls back to newest N if no source.
 */
function chainWindowAroundSource(
  messagesNewestFirst: ThreadMessage[],
  sourceId: string | null,
  windowSize = 3,
): ThreadMessage[] {
  if (!messagesNewestFirst.length) return []
  const chrono = [...messagesNewestFirst].reverse()
  let end = chrono.length - 1
  if (sourceId) {
    const idx = chrono.findIndex((m) => m.id === sourceId)
    if (idx >= 0) end = idx
  }
  const start = Math.max(0, end - (windowSize - 1))
  return chrono.slice(start, end + 1).reverse()
}

function ThreadPanel({
  taskId,
  sourceQuote,
}: {
  taskId: string
  sourceQuote: string
}) {
  const [open, setOpen] = useState(false)
  const [thread, setThread] = useState<TaskThread | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setOpen(false)
    setThread(null)
    setError(null)
    setLoading(false)
  }, [taskId])

  const loadThread = () => {
    if (loading) return
    setLoading(true)
    setError(null)
    void fetchTaskThread(taskId)
      .then((data) => {
        setThread(data)
      })
      .catch((e) => {
        setThread(null)
        setError(e instanceof Error ? e.message : 'failed to load thread')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next && !thread && !loading) loadThread()
  }

  const sourceId = thread
    ? findSourceMessageId(sourceQuote, thread.messages)
    : null
  const visible = thread
    ? chainWindowAroundSource(thread.messages, sourceId, 3)
    : []

  return (
    <section className={`thread-panel${open ? ' is-open' : ''}`}>
      <div className="thread-panel-head">
        <button
          type="button"
          className="thread-toggle"
          aria-expanded={open}
          onClick={toggle}
        >
          {open ? 'Hide thread' : 'Show thread'}
          <span className="thread-toggle-hint">last 3 around todo source</span>
        </button>
        {open && thread ? (
          <a
            className="gmail-secondary"
            href={gmailThreadUrl(thread.gmail_thread_id)}
            target="_blank"
            rel="noreferrer"
          >
            Open in Gmail
          </a>
        ) : null}
      </div>
      {open ? (
        <div className="thread-panel-body">
          {loading ? <p className="thread-empty">Loading stored mail…</p> : null}
          {error ? (
            <p className="thread-empty">
              {error}{' '}
              <button type="button" className="text-retry" onClick={loadThread}>
                Retry
              </button>
            </p>
          ) : null}
          {thread?.thread_access === 'redacted' ? (
            <p className="thread-empty">
              Monitor access: headers only — email bodies are hidden.
            </p>
          ) : null}
          {!loading && !error && thread && !visible.length ? (
            <p className="thread-empty">No messages in Neon for this thread yet.</p>
          ) : null}
          {!loading
            ? visible.map((m) => (
                <ThreadMessageBox
                  key={m.id}
                  message={m}
                  isSource={m.id === sourceId}
                />
              ))
            : null}
        </div>
      ) : null}
    </section>
  )
}

type DetailProps = {
  task: EmailTask
  childrenTasks: EmailTask[]
}

export function TaskDetailView({ task, childrenTasks }: DetailProps) {
  const navigate = useNavigate()
  const {
    pending,
    refresh,
    setStatus,
    setPriority,
    setOwner,
    setDueAt,
    setExtraInstructions,
    optimistic,
    getTask,
  } = useTasks()
  const [menuOpen, setMenuOpen] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  const [retryStatus, setRetryStatus] = useState<string | null>(null)
  const [ellisOpen, setEllisOpen] = useState(false)
  const [closeParent, setCloseParent] = useState<EmailTask | null>(null)
  const [reopenChildren, setReopenChildren] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const prevAllChildrenDone = useRef(false)
  const seededChildren = useRef(false)
  const uiLocked = retrying || ellisOpen
  const terminal =
    task.status === 'done' ||
    task.status === 'dismissed' ||
    task.status === 'wrong_redo'
  const role = task.my_role ?? 'owner'
  const canPatchLight =
    role === 'owner' ||
    role === 'collaborator' ||
    role === 'unscoped'
  const canMutateHeavy = role === 'owner' || role === 'unscoped'
  const fieldsLocked = pending || uiLocked || terminal || !canPatchLight
  const heavyLocked = fieldsLocked || !canMutateHeavy

  const maybePromptCloseParent = (childJustDone: EmailTask) => {
    const parentId = childJustDone.parent_id
    if (!parentId) return
    const parent = getTask(parentId)
    if (!parent || parent.status !== 'open') return
    const siblings = optimistic.filter((t) => t.parent_id === parentId)
    const allClosed = siblings.every((s) => {
      const status = s.id === childJustDone.id ? 'done' : s.status
      return status === 'done' || status === 'dismissed'
    })
    if (allClosed && siblings.length > 0) setCloseParent(parent)
  }

  // Parent page: when children flip to all closed while viewing, offer closing.
  useEffect(() => {
    seededChildren.current = false
    prevAllChildrenDone.current = false
  }, [task.id])

  useEffect(() => {
    if (task.status !== 'open' || !childrenTasks.length) {
      prevAllChildrenDone.current = false
      return
    }
    const allClosed = childrenTasks.every(
      (c) => c.status === 'done' || c.status === 'dismissed',
    )
    if (!seededChildren.current) {
      seededChildren.current = true
      prevAllChildrenDone.current = allClosed
      return
    }
    if (allClosed && !prevAllChildrenDone.current) {
      setCloseParent(task)
    }
    prevAllChildrenDone.current = allClosed
  }, [childrenTasks, task])

  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [menuOpen])

  const onRetryAcquisition = () => {
    if (retrying || task.status === 'wrong_redo' || ellisOpen) return
    setMenuOpen(false)
    setRetryError(null)
    setRetryStatus('Starting…')
    setRetrying(true)
    void retryTaskAcquisition(task.id, (ev) => {
      if (ev.message) setRetryStatus(ev.message)
    })
      .then(async (result) => {
        await refresh()
        const next = result.root_task_id
        if (next) navigate(`/tasks/${next}`)
        else if (!result.created_task_ids.length) {
          setRetryError('No new tasks were captured — try again or check the source email.')
        }
      })
      .catch((e) => {
        setRetryError(e instanceof Error ? e.message : 'retry_failed')
      })
      .finally(() => {
        setRetrying(false)
        setRetryStatus(null)
      })
  }

  const onReopen = () => {
    setMenuOpen(false)
    const closedKids = childrenTasks.filter(
      (c) => c.status === 'done' || c.status === 'dismissed',
    )
    if (!task.parent_id && closedKids.length > 0 && task.status !== 'open') {
      setReopenChildren(true)
      return
    }
    void setStatus(task, 'open')
  }

  const confirmReopenParentOnly = () => {
    setReopenChildren(false)
    void setStatus(task, 'open')
  }

  const confirmReopenWithChildren = () => {
    setReopenChildren(false)
    void setStatus(task, 'open')
    for (const c of childrenTasks) {
      if (c.status === 'done' || c.status === 'dismissed') {
        void setStatus(c, 'open')
      }
    }
  }

  return (
    <article className="detail-card">
      <div className="detail-head">
        <span className={`status status-${task.status}`}>{statusLabel(task.status)}</span>
        {task.task_type === 'personal' ? (
          <span className="pill muted">personal</span>
        ) : null}
        {task.my_role && task.my_role !== 'owner' && task.my_role !== 'unscoped' ? (
          <span className="pill muted" title="Your access on this task">
            {task.my_role}
          </span>
        ) : null}
        <h1>{task.title}</h1>
        <div className="detail-overflow" ref={menuRef}>
          <button
            type="button"
            className="overflow-trigger"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Task actions"
            disabled={uiLocked}
            onClick={() => setMenuOpen((o) => !o)}
          >
            ⋮
          </button>
          {menuOpen ? (
            <ul className="overflow-menu" role="menu">
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={uiLocked || task.status === 'wrong_redo'}
                  onClick={onRetryAcquisition}
                >
                  Retry Task Capture
                </button>
              </li>
              {task.status !== 'open' ? (
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    disabled={uiLocked}
                    title="Bring this todo back to open"
                    onClick={onReopen}
                  >
                    Reopen
                  </button>
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      </div>

      {retrying ? (
        <p className="retry-banner" role="status">
          {retryStatus ?? 'Re-acquiring tasks with Ellis…'}
        </p>
      ) : null}
      {retryError ? (
        <p className="retry-banner is-error" role="alert">
          Retry failed: {retryError}
        </p>
      ) : null}

      {task.detail ? <p className="detail-prose">{task.detail}</p> : null}
      {task.source_quote ? (
        <blockquote className="quote">{task.source_quote}</blockquote>
      ) : null}

      <details className="detail-accordion detail-edit" aria-label="Editable task fields">
        <summary className="detail-accordion-summary">
          <span className="detail-accordion-title">Details</span>
          <span className="detail-accordion-meta">
            {[
              task.owner_email?.trim() || 'No owner',
              dueDateInputValue(task.due_at) || 'No due',
              task.priority ?? 'No priority',
            ].join(' · ')}
          </span>
        </summary>
        <div className="detail-accordion-body">
          <div className="edit-fields">
            <div className="edit-field">
              <label htmlFor={`owner-${task.id}`}>Owner</label>
              <input
                id={`owner-${task.id}`}
                type="email"
                className="edit-control"
                defaultValue={task.owner_email ?? ''}
                key={`owner-${task.id}-${task.owner_email ?? ''}`}
                disabled={heavyLocked}
                placeholder="Who should do this?"
                onBlur={(e) => setOwner(task, e.target.value)}
              />
            </div>
            <div className="edit-field">
              <label htmlFor={`due-${task.id}`}>Due</label>
              <input
                id={`due-${task.id}`}
                type="date"
                className="edit-control"
                value={dueDateInputValue(task.due_at)}
                disabled={fieldsLocked}
                onChange={(e) => setDueAt(task, e.target.value)}
              />
            </div>
            <div className="edit-field">
              <label htmlFor={`priority-${task.id}`}>Priority</label>
              <select
                id={`priority-${task.id}`}
                className="edit-control"
                value={task.priority ?? ''}
                disabled={fieldsLocked}
                onChange={(e) => setPriority(task, e.target.value as TaskPriority | '')}
              >
                <option value="">unset</option>
                <option value="low">low</option>
                <option value="normal">normal</option>
                <option value="high">high</option>
                <option value="urgent">urgent</option>
              </select>
            </div>
          </div>
          <div className="edit-field edit-field-full">
            <label htmlFor={`notes-${task.id}`}>Extra instructions</label>
            <textarea
              id={`notes-${task.id}`}
              className="edit-control edit-textarea"
              rows={3}
              disabled={heavyLocked}
              placeholder="Notes for Ellis or yourself…"
              defaultValue={extraInstructions(task)}
              key={`notes-${task.id}-${extraInstructions(task)}`}
              onBlur={(e) => setExtraInstructions(task, e.target.value)}
            />
          </div>
        </div>
      </details>

      <TaskShares taskId={task.id} myRole={task.my_role} />

      <ThreadPanel taskId={task.id} sourceQuote={task.source_quote} />

      <SubtasksSection parent={task} childrenTasks={childrenTasks} />

      <div className="detail-actions">
        <button
          type="button"
          className="ellis"
          disabled={
            uiLocked ||
            !canMutateHeavy ||
            task.status !== 'open' ||
            childrenTasks.some((c) => c.status === 'open')
          }
          title={
            childrenTasks.some((c) => c.status === 'open')
              ? 'Open a leaf sub-todo — parents are out of scope for v1'
              : 'Draft a reply-all with Ellis'
          }
          onClick={() => setEllisOpen(true)}
        >
          Have Ellis do it!
        </button>
        {task.status !== 'done' && task.status !== 'wrong_redo' ? (
          <button
            type="button"
            disabled={uiLocked}
            onClick={() => {
              void setStatus(task, 'done').then((updated) => {
                if (updated) maybePromptCloseParent(updated)
              })
            }}
          >
            Done
          </button>
        ) : null}
        {task.status !== 'dismissed' && task.status !== 'wrong_redo' ? (
          <button
            type="button"
            className="quiet"
            disabled={uiLocked}
            onClick={() => void setStatus(task, 'dismissed')}
          >
            Dismiss
          </button>
        ) : null}
      </div>

      <footer className="detail-meta">
        <dl className="meta-list">
          <div className="meta-item">
            <dt>Assigned by</dt>
            <dd>{task.assigned_by_email || '—'}</dd>
          </div>
          <div className="meta-item">
            <dt>Created</dt>
            <dd>{formatWhen(task.created_at)}</dd>
          </div>
          <div className="meta-item">
            <dt>Last seen</dt>
            <dd>{relativeTime(task.last_seen_at)}</dd>
          </div>
          <div className="meta-item">
            <dt>Inbox</dt>
            <dd>{task.inbox_address}</dd>
          </div>
        </dl>
      </footer>

      <EllisExecuteModal
        taskId={task.id}
        open={ellisOpen}
        onClose={() => setEllisOpen(false)}
        onSent={async () => {
          await refresh()
        }}
      />

      {closeParent ? (
        <div className="ellis-modal-backdrop" role="presentation">
          <div
            className="ellis-modal close-parent-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="close-parent-title"
          >
            <header className="ellis-modal-head">
              <h2 id="close-parent-title">Close parent todo?</h2>
              <p className="ellis-modal-sub">
                All sub-todos are finished. Mark{' '}
                <strong>{closeParent.title}</strong> done too?
              </p>
            </header>
            <footer className="ellis-modal-actions">
              <button
                type="button"
                className="ellis"
                disabled={pending}
                onClick={() => {
                  const parent = closeParent
                  setCloseParent(null)
                  void setStatus(parent, 'done')
                  if (task.id !== parent.id) navigate(`/tasks/${parent.id}`)
                }}
              >
                Yes, mark parent done
              </button>
              <button
                type="button"
                className="quiet"
                onClick={() => setCloseParent(null)}
              >
                Not now
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {reopenChildren ? (
        <div className="ellis-modal-backdrop" role="presentation">
          <div
            className="ellis-modal close-parent-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reopen-kids-title"
          >
            <header className="ellis-modal-head">
              <h2 id="reopen-kids-title">Reopen sub-todos too?</h2>
              <p className="ellis-modal-sub">
                <strong>{task.title}</strong> has finished sub-todos. Bring them
                back to open with the parent?
              </p>
            </header>
            <footer className="ellis-modal-actions">
              <button
                type="button"
                className="ellis"
                disabled={pending}
                onClick={confirmReopenWithChildren}
              >
                Yes, reopen all
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={confirmReopenParentOnly}
              >
                Parent only
              </button>
              <button
                type="button"
                className="quiet"
                onClick={() => setReopenChildren(false)}
              >
                Cancel
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </article>
  )
}
